import type { CaseStage } from '../../api/types';
import { useI18n, useT } from '../../i18n';
import { loc } from '../../lib/loc';

export type StageMark = 'done' | 'current' | 'next';

/** done/current flags from the API; anything after the current stage is "next". */
export function stageMarks(states: CaseStage[]): StageMark[] {
  return states.map((s) => (s.current ? 'current' : s.done ? 'done' : 'next'));
}

/** Vertical list of stages with dot markers (the "Етапи" block of Admin-Cases). */
export function StageProgress({ states, compact = false }: { states: CaseStage[]; compact?: boolean }) {
  const t = useT();
  const locale = useI18n((s) => s.locale);
  const marks = stageMarks(states);
  if (!states.length) return null;

  if (compact) {
    const done = marks.filter((m) => m === 'done').length;
    const cur = marks.indexOf('current');
    const pct = Math.round(((done + (cur >= 0 ? 0.5 : 0)) / states.length) * 100);
    return (
      <div className="mt-5 h-0.5 bg-line" aria-hidden>
        <i className="block h-0.5 bg-fg" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    );
  }

  return (
    <ol className="mt-4">
      {states.map((s, i) => {
        const m = marks[i] ?? 'next';
        return (
          <li
            key={s.key}
            aria-current={m === 'current' ? 'step' : undefined}
            className={`row flex min-h-[48px] items-center gap-4 py-2 text-[16px] ${m === 'next' ? 'text-lab' : ''} ${m === 'current' ? 'font-medium' : ''}`}
          >
            <i className={`mk ${m === 'done' ? 'mk-done' : m === 'current' ? 'mk-cur' : 'mk-next'}`} aria-hidden />
            <span className="min-w-0 flex-1">{loc(s.name, locale) || s.key}</span>
            <span className="text-[13px] font-normal text-lab">
              {t(m === 'done' ? 'case.stage.done' : m === 'current' ? 'case.stage.current' : 'case.stage.next')}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
